import React from 'react';
import {
  ChatEntryAuthorDto,
  ChatEntryDto,
  FinalPromptType,
  PostProcessJobStatusDto,
  PostProcessJobTypeDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import LoadingEllipsis from '../../components/loading-ellipsis/LoadingEllipsis.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { getPostProcessJob, isPostProcessJobInProgress } from './ai-chat.utilities';

export type PostProcessJobAction = {
  label: string;
  activeLabel: string;
  isActive: boolean;
  onClick: (entryId: number) => void;
};

export type PostProcessJobSectionProps = {
  entry: ChatEntryDto;
  finalPromptType: FinalPromptType;
  jobType: PostProcessJobTypeDto;
  brandName: string;
  loadingLabel: string;
  errorFallback: string;
  actions: PostProcessJobAction[];
};

const PostProcessJobSection = (props: PostProcessJobSectionProps): JSX.Element => {
  const {
    entry,
    finalPromptType,
    jobType,
    brandName,
    loadingLabel,
    errorFallback,
    actions,
  } = props;

  if (entry.author !== ChatEntryAuthorDto.BOT || entry.finalPromptType !== finalPromptType) {
    return null;
  }

  const job = getPostProcessJob(entry, jobType);
  if (!job) {
    return null;
  }

  const isInProgress = isPostProcessJobInProgress(job);
  const isError = job.status === PostProcessJobStatusDto.ERROR
    || job.status === PostProcessJobStatusDto.CANCELLED_TIMED_OUT
    || job.status === PostProcessJobStatusDto.CANCELLED_ADMIN_REQUEST;

  return (
    <div className="c-els-ai-chat__job-action-section u-els-margin-top-1o2">
      {isInProgress ? (
        <div className="c-els-card">
          <div className="c-els-card__content">
            <div>
              <LoadingEllipsis text={loadingLabel} />
            </div>
            <div className="u-els-margin-top u-els-font-size-caption">
              {brandName} is working hard! This can take up to a minute.
            </div>
          </div>
        </div>
      ) : isError ? (
        <div className="c-els-card">
          <div className="c-els-card__content">
            <div className="u-els-color-negative">{job.errorMessage || errorFallback}</div>
          </div>
        </div>
      ) : (
        <FlexLayout modifiers={[
          FlexLayoutModifier.GUTTERS,
          FlexLayoutModifier.WRAP,
          FlexLayoutModifier.LEFT,
        ]}>
          {actions.map((action) => (
            // The active state stays the same button, just greyed, so only its
            // colors change — swapping in a card would shift padding and border
            // width and make the section jump.
            <FlexItem key={action.label}>
              <button
                type="button"
                className="c-els-ai-chat__job-action c-els-button c-els-button--small c-els-button--secondary"
                // aria-disabled rather than disabled: the active button keeps
                // its place in the tab order so a keyboard or screen-reader
                // user can still find it and hear which panel is open.
                aria-disabled={action.isActive}
                onClick={() => {
                  if (action.isActive) {
                    return;
                  }
                  action.onClick(entry.id);
                }}
              >
                <span>{action.isActive ? action.activeLabel : action.label}</span>
              </button>
            </FlexItem>
          ))}
        </FlexLayout>
      )}
    </div>
  );
};

export default PostProcessJobSection;
